import { parseISO, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import { differenceInDays } from 'date-fns';
import { Cruise } from '../context/CruiseContext';
import { ShipMetadata } from '../context/CruiseMetadataContext';

export interface RewindStats {
  cruisesCompleted: number;
  nightsAtSea: number;
  portsVisited: number;
  firstTimeCruiseLines: string[];
  firstTimeShips: Array<{
    shipName: string;
    cruiseLine: string;
    imageUrl: string | null;
    grossTonnage?: string;
    yearBuilt?: string;
    shipClass?: string;
  }>;
  firstTimePorts: string[];
  shipImages: Array<{
    cruiseLine: string;
    shipName: string;
    imageUrl: string | null;
    returnDate: string; // For sorting by most recent
    isNewShip: boolean; // Whether this ship was sailed for the first time ever
  }>;
  shipHighlights?: {
    largest?: {
      shipName: string;
      cruiseLine: string;
      tonnage: string;
      imageUrl: string | null;
    };
    smallest?: {
      shipName: string;
      cruiseLine: string;
      tonnage: string;
      imageUrl: string | null;
    };
    oldest?: {
      shipName: string;
      cruiseLine: string;
      yearBuilt: string;
      imageUrl: string | null;
    };
    newest?: {
      shipName: string;
      cruiseLine: string;
      yearBuilt: string;
      imageUrl: string | null;
    };
  };
  cruiseLineBreakdown: Array<{
    cruiseLine: string;
    count: number;
  }>;
  cruiseStats: {
    longest: {
      nights: number;
      shipName: string;
      cruiseLine: string;
    } | null;
    shortest: {
      nights: number;
      shipName: string;
      cruiseLine: string;
    } | null;
    averageNights: number;
  };
  totalTonnage?: number; // Sum of all ship tonnages (in GRT)
}

/**
 * Calculates year-in-review statistics for all cruises in a given calendar year
 * @param cruises - Array of all cruises
 * @param year - Year to calculate stats for
 * @param getShipImageUrl - Function to get ship image URL
 * @param getShipMetadata - Function to get ship metadata
 * @returns RewindStats object with year-specific statistics
 */
export const calculateRewindStats = (
  cruises: Cruise[],
  year: number,
  getShipImageUrl: (cruiseLine: string, shipName: string) => string | null,
  getShipMetadata: (cruiseLine: string, shipName: string) => ShipMetadata | null
): RewindStats => {
  const yearStart = startOfYear(new Date(year, 0, 1));
  const yearEnd = endOfYear(new Date(year, 0, 1));

  // Filter ALL cruises in the specified calendar year (departure or return date within the year)
  // This includes both completed and upcoming cruises
  const cruisesThisYear = cruises.filter(cruise => {
    const departureDate = parseISO(cruise.departureDate);
    const returnDate = parseISO(cruise.returnDate);
    // Include if departure date OR return date falls within the calendar year
    return isWithinInterval(departureDate, { start: yearStart, end: yearEnd }) ||
           isWithinInterval(returnDate, { start: yearStart, end: yearEnd });
  });

  // Get all cruises with return dates before this year (for "new" comparisons)
  // Include both completed and non-completed cruises for accurate "new" detection
  const previousCruises = cruises.filter(cruise => {
    const returnDate = parseISO(cruise.returnDate);
    return returnDate < yearStart;
  });

  // Collect all previously sailed cruise lines, ships, and ports
  const previousCruiseLines = new Set<string>();
  const previousShips = new Set<string>();
  const previousPorts = new Set<string>();

  previousCruises.forEach(cruise => {
    previousCruiseLines.add(cruise.cruiseLine);
    previousShips.add(cruise.shipName.toLowerCase());
    cruise.ports?.forEach(port => {
      previousPorts.add(port.name.toLowerCase());
    });
  });

  // Calculate stats for this year (using ALL cruises in the year, not just completed)
  let nightsAtSea = 0;
  const allPortsVisited = new Set<string>(); // Track all unique ports visited this year
  const firstTimeCruiseLines = new Set<string>();
  const firstTimeShipsMap = new Map<string, {
    shipName: string;
    cruiseLine: string;
    imageUrl: string | null;
    grossTonnage?: string;
    yearBuilt?: string;
    shipClass?: string;
  }>();
  const firstTimePorts = new Set<string>();
  const shipImages: Array<{ cruiseLine: string; shipName: string; imageUrl: string | null; returnDate: string; isNewShip: boolean }> = [];
  
  // For ship highlights and cruise stats
  const cruiseNights: Array<{ nights: number; shipName: string; cruiseLine: string }> = [];
  const cruiseLineCounts = new Map<string, number>();
  const shipsWithMetadata: Array<{
    shipName: string;
    cruiseLine: string;
    grossTonnage?: number;
    yearBuilt?: string;
    imageUrl: string | null;
  }> = [];
  let totalTonnage = 0;

  cruisesThisYear.forEach(cruise => {
    const departureDate = parseISO(cruise.departureDate);
    const returnDate = parseISO(cruise.returnDate);
    const nights = differenceInDays(returnDate, departureDate);
    nightsAtSea += nights;
    cruiseNights.push({ nights, shipName: cruise.shipName, cruiseLine: cruise.cruiseLine });

    // Cruise line breakdown
    const currentCount = cruiseLineCounts.get(cruise.cruiseLine) || 0;
    cruiseLineCounts.set(cruise.cruiseLine, currentCount + 1);

    // Check for first time cruise lines
    if (!previousCruiseLines.has(cruise.cruiseLine)) {
      firstTimeCruiseLines.add(cruise.cruiseLine);
    }

    // Check for first time ships
    const shipKey = cruise.shipName.toLowerCase();
    const isFirstTimeShip = !previousShips.has(shipKey);
    if (isFirstTimeShip) {
      const imageUrl = getShipImageUrl(cruise.cruiseLine, cruise.shipName);
      const shipMetadata = getShipMetadata(cruise.cruiseLine, cruise.shipName);
      
      if (!firstTimeShipsMap.has(shipKey)) {
        firstTimeShipsMap.set(shipKey, {
          shipName: cruise.shipName,
          cruiseLine: cruise.cruiseLine,
          imageUrl,
          grossTonnage: shipMetadata?.grossTonnage,
          yearBuilt: shipMetadata?.yearBuilt,
          shipClass: shipMetadata?.shipClass
        });
      }
    }

    // Check for first time ports and collect all ports visited
    cruise.ports?.forEach(port => {
      const portKey = port.name.toLowerCase();
      // Add to all ports visited (for total count)
      allPortsVisited.add(portKey);
      // Check if it's a first-time port
      if (!previousPorts.has(portKey)) {
        firstTimePorts.add(port.name);
      }
    });

    // Collect ship images (collect all, no limit for page display)
    const imageUrl = getShipImageUrl(cruise.cruiseLine, cruise.shipName);
    
    // Only add if we don't already have this ship image
    const existingShip = shipImages.find(
      img => img.shipName.toLowerCase() === cruise.shipName.toLowerCase() && 
             img.cruiseLine === cruise.cruiseLine
    );
    if (!existingShip) {
      shipImages.push({
        cruiseLine: cruise.cruiseLine,
        shipName: cruise.shipName,
        imageUrl,
        returnDate: cruise.returnDate,
        isNewShip: isFirstTimeShip
      });
    }

    // Collect ship metadata for highlights and tonnage calculation
    const shipMetadata = getShipMetadata(cruise.cruiseLine, cruise.shipName);
    if (shipMetadata) {
      const existingShipMeta = shipsWithMetadata.find(
        s => s.shipName.toLowerCase() === cruise.shipName.toLowerCase() &&
             s.cruiseLine === cruise.cruiseLine
      );
      if (!existingShipMeta) {
        const tonnage = shipMetadata.grossTonnage 
          ? parseFloat(shipMetadata.grossTonnage.replace(/,/g, ''))
          : undefined;
        if (tonnage && !isNaN(tonnage)) {
          totalTonnage += tonnage;
        }
        shipsWithMetadata.push({
          shipName: cruise.shipName,
          cruiseLine: cruise.cruiseLine,
          grossTonnage: tonnage,
          yearBuilt: shipMetadata.yearBuilt,
          imageUrl
        });
      }
    }
  });

  // Calculate cruise stats
  const longestCruise = cruiseNights.length > 0 
    ? cruiseNights.reduce((max, cruise) => cruise.nights > max.nights ? cruise : max)
    : null;
  const shortestCruise = cruiseNights.length > 0
    ? cruiseNights.reduce((min, cruise) => cruise.nights < min.nights ? cruise : min)
    : null;
  const averageNights = cruiseNights.length > 0
    ? Math.round(nightsAtSea / cruiseNights.length)
    : 0;

  // Calculate ship highlights
  const shipsWithTonnage = shipsWithMetadata.filter(s => s.grossTonnage !== undefined);
  const shipsWithYearBuilt = shipsWithMetadata.filter(s => s.yearBuilt);
  
  const largestShip = shipsWithTonnage.length > 0
    ? shipsWithTonnage.reduce((max, ship) => (ship.grossTonnage || 0) > (max.grossTonnage || 0) ? ship : max)
    : undefined;
  const smallestShip = shipsWithTonnage.length > 0
    ? shipsWithTonnage.reduce((min, ship) => (ship.grossTonnage || 0) < (min.grossTonnage || 0) ? ship : min)
    : undefined;
  
  const oldestShip = shipsWithYearBuilt.length > 0
    ? shipsWithYearBuilt.reduce((oldest, ship) => {
        const shipYear = parseInt(ship.yearBuilt || '9999');
        const oldestYear = parseInt(oldest.yearBuilt || '9999');
        return shipYear < oldestYear ? ship : oldest;
      })
    : undefined;
  const newestShip = shipsWithYearBuilt.length > 0
    ? shipsWithYearBuilt.reduce((newest, ship) => {
        const shipYear = parseInt(ship.yearBuilt || '0');
        const newestYear = parseInt(newest.yearBuilt || '0');
        return shipYear > newestYear ? ship : newest;
      })
    : undefined;

  // Build ship highlights object
  const shipHighlights: RewindStats['shipHighlights'] = {};
  if (largestShip) {
    shipHighlights.largest = {
      shipName: largestShip.shipName,
      cruiseLine: largestShip.cruiseLine,
      tonnage: largestShip.grossTonnage!.toLocaleString('en-US'),
      imageUrl: largestShip.imageUrl
    };
  }
  if (smallestShip) {
    shipHighlights.smallest = {
      shipName: smallestShip.shipName,
      cruiseLine: smallestShip.cruiseLine,
      tonnage: smallestShip.grossTonnage!.toLocaleString('en-US'),
      imageUrl: smallestShip.imageUrl
    };
  }
  if (oldestShip) {
    shipHighlights.oldest = {
      shipName: oldestShip.shipName,
      cruiseLine: oldestShip.cruiseLine,
      yearBuilt: oldestShip.yearBuilt!,
      imageUrl: oldestShip.imageUrl
    };
  }
  if (newestShip) {
    shipHighlights.newest = {
      shipName: newestShip.shipName,
      cruiseLine: newestShip.cruiseLine,
      yearBuilt: newestShip.yearBuilt!,
      imageUrl: newestShip.imageUrl
    };
  }

  // Build cruise line breakdown
  const cruiseLineBreakdown = Array.from(cruiseLineCounts.entries())
    .map(([cruiseLine, count]) => ({ cruiseLine, count }))
    .sort((a, b) => b.count - a.count);

  return {
    cruisesCompleted: cruisesThisYear.length, // Count all cruises in the calendar year (completed + upcoming)
    nightsAtSea,
    portsVisited: allPortsVisited.size, // Count of unique ports visited
    firstTimeCruiseLines: Array.from(firstTimeCruiseLines),
    firstTimeShips: Array.from(firstTimeShipsMap.values()),
    firstTimePorts: Array.from(firstTimePorts),
    shipImages,
    shipHighlights: Object.keys(shipHighlights).length > 0 ? shipHighlights : undefined,
    cruiseLineBreakdown,
    cruiseStats: {
      longest: longestCruise,
      shortest: shortestCruise,
      averageNights
    },
    totalTonnage: totalTonnage > 0 ? totalTonnage : undefined
  };
};

